import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { contentAPI } from '../services/api';
import ReactMarkdown from 'react-markdown';
import useNavigationWarning from '../hooks/useNavigationWarning';
import { checkAuthBeforeAction } from '../utils/authHelper';
import NavigationWarningModal from '../components/NavigationWarningModal';
import usePermission from '../hooks/usePermission';
import UpgradePrompt from '../components/UpgradePrompt';

// 图片组件 - 带加载状态
const BlogImage = (props) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="my-6">
      {!loaded && (
        <div className="w-full h-64 bg-gray-200 rounded-lg animate-pulse flex items-center justify-center">
          <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
      <img
        {...props}
        className={`w-full rounded-lg shadow-md transition-opacity duration-300 ${
          loaded ? 'opacity-100' : 'opacity-0 hidden'
        }`}
        alt={props.alt || 'Blog image'}
        onLoad={() => setLoaded(true)}
        loading="eager"
      />
    </div>
  );
};

const BlogPosts = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Permission check
  const { hasFeatureAccess, getPlanInfo, getRequiredPlanForFeature } = usePermission();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradePromptData, setUpgradePromptData] = useState({});

  const [formData, setFormData] = useState({
    blogType: 'how-to',
    topic: '',
    targetAudience: '',
    mainPoints: '',
    keywords: '',
    wordCount: '1000-1500',
    tone: 'informative',
    writingStyle: 'conversational',
    includeIntro: true,
    includeConclusion: true,
    includeFAQ: false,
    includeCTA: true,
    ctaType: 'read-more',
    seoOptimized: true,
    contentMode: 'text-only', // 新增: 'text-only' 或 'text-with-images'
    language: 'en' // 新增: 语言选择
  });

  const [generatedContent, setGeneratedContent] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');

  // Show warning when user tries to leave during generation
  const navigationWarning = useNavigationWarning(isGenerating, 'Blog post is being generated. If you leave now, the generation will continue in the background. Are you sure you want to leave?', 'content-generation');

  const [error, setError] = useState('');
  const [copiedStates, setCopiedStates] = useState({}); // 追踪哪些内容已复制

  // Handle prefilled data from Workspace
  useEffect(() => {
    if (location.state?.prefillData) {
      const prefillData = location.state.prefillData;
      setFormData(prev => ({
        ...prev,
        topic: prefillData.topic || prev.topic,
        blogType: prefillData.blogType || prev.blogType,
        targetAudience: prefillData.targetAudience || prev.targetAudience,
        wordCount: prefillData.wordCount || prev.wordCount,
        tone: prefillData.tone || prev.tone,
        writingStyle: prefillData.writingStyle || prev.writingStyle,
        language: prefillData.language || prev.language,
        keywords: prefillData.keywords || prev.keywords
      }));

      // If there's generated content, display it
      if (prefillData.generatedContent) {
        setGeneratedContent({
          title: prefillData.generatedTitle || '',
          fullContent: prefillData.generatedContent || '',
          seoMeta: prefillData.generatedSeoMeta || null,
          stats: {
            wordCount: prefillData.generatedContent ? prefillData.generatedContent.split(/\s+/).length : 0,
            readingTime: prefillData.generatedContent ? `${Math.ceil(prefillData.generatedContent.split(/\s+/).length / 200)} min` : '0 min'
          }
        });
      }
    }
  }, [location.state]);

  // SVG图标组件
  const HowToIcon = ({ color = 'currentColor' }) => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M648.2944 75.264a22.6304 22.6304 0 0 1 12.6464 3.84l3.4816 2.8672 228.4032 228.4032a23.2448 23.2448 0 0 1 6.2976 11.6224l0.4096 4.5056v457.0624a98.9696 98.9696 0 0 1-90.4704 98.6112l-8.5504 0.3584h-236.3392a22.8352 22.8352 0 0 1-4.608-45.2096l4.5568-0.512h236.3904a53.2992 53.2992 0 0 0 52.8896-46.592l0.4096-6.7072v-447.488l-215.04-215.04h-295.0144a53.2992 53.2992 0 0 0-52.8896 46.592l-0.4096 6.7072v455.9872a22.8352 22.8352 0 0 1-45.2096 4.608l-0.512-4.608v-455.9872a98.9696 98.9696 0 0 1 90.4704-98.6112l8.5504-0.3584z" fill={color} />
      <path d="M467.6608 440.4224a176.6912 176.6912 0 0 0-36.1984 166.2976l2.5088 7.68-166.6048 166.5536a77.2096 77.2096 0 0 0 0 109.2608l6.2976 5.5808a77.2608 77.2608 0 0 0 102.912-5.5808l166.6048-166.656 7.7312 2.56a176.64 176.64 0 0 0 211.0976-242.2272 22.8352 22.8352 0 0 0-36.9664-6.7072l-96.6656 96.5632a2.7136 2.7136 0 0 1-3.7376 0l-40.96-40.96a2.816 2.816 0 0 1 0-3.7888l96.8192-96.6144a22.8352 22.8352 0 0 0-6.7072-36.9664 176.64 176.64 0 0 0-197.8368 35.84l-8.2944 8.9088z m262.7072 95.9488l1.0752 7.3728a130.9696 130.9696 0 0 1-184.32 132.3008 22.8352 22.8352 0 0 0-25.6 4.7104l-177.2032 177.1008a31.5392 31.5392 0 1 1-44.5952-44.5952l177.1008-177.2032a22.9376 22.9376 0 0 0 4.7104-25.6 130.9696 130.9696 0 0 1 26.7264-146.6368l7.936-7.3216a131.0208 131.0208 0 0 1 97.6384-30.464l7.3216 1.024-69.9392 69.7856a48.5376 48.5376 0 0 0 0 68.0448l40.96 41.216a48.5376 48.5376 0 0 0 68.0448 0z" fill={color} />
    </svg>
  );

  const ListicleIcon = ({ color = 'currentColor' }) => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M854.6 288.6L639.4 73.4c-6-6-14.1-9.4-22.6-9.4H192c-17.7 0-32 14.3-32 32v832c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V311.3c0-8.5-3.4-16.7-9.4-22.7zM790.2 326H602V137.8L790.2 326z m1.8 562H232V136h302v216c0 23.2 18.8 42 42 42h216v494z" fill={color} />
      <path d="M514.1 580.1l-61.8-102.4c-2.2-3.6-6.1-5.8-10.3-5.8h-38.4c-2.3 0-4.5 0.6-6.4 1.9-5.6 3.5-7.3 10.9-3.7 16.6l82.3 130.4-83.4 132.8c-1.2 1.9-1.8 4.1-1.8 6.4 0 6.6 5.4 12 12 12h34.5c4.2 0 8-2.2 10.2-5.7L510 664.8l62.3 101.4c2.2 3.6 6.1 5.7 10.2 5.7H620c2.3 0 4.5-0.7 6.5-1.9 5.6-3.6 7.2-11 3.6-16.6l-84-130.4 85.3-132.5c1.2-1.9 1.9-4.2 1.9-6.5 0-6.6-5.4-12-12-12h-35.7c-4.2 0-8.1 2.2-10.3 5.8l-61.2 102.3z" fill={color} />
    </svg>
  );

  const CaseStudyIcon = ({ color = 'currentColor' }) => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M543.7 459.7l-97.6-209.2c-5.1-11-16.4-17.2-27.9-16.5-11.5-0.7-22.7 5.5-27.9 16.5l-97.6 209.2c-6.7 14.4-0.4 31.6 14 38.3 14.4 6.7 31.6 0.4 38.4-14l20.8-44.7h104.6l20.8 44.7c6.7 14.4 24 20.7 38.4 14 14.4-6.6 20.7-23.9 14-38.3z m-150.9-78l25.4-54.4 25.4 54.4h-50.8zM728.4 295.1h-28.9v-28.9c0-15.9-12.9-28.9-28.9-28.9s-28.9 12.9-28.9 28.9v28.9H613c-15.9 0-28.9 12.9-28.9 28.9 0 15.9 12.9 28.9 28.9 28.9h28.9v28.9c0 15.9 12.9 28.9 28.9 28.9s28.9-12.9 28.9-28.9v-28.9h28.9c15.9 0 28.9-12.9 28.9-28.9-0.2-15.9-13.1-28.9-29.1-28.9zM553.3 627H297.6c-1.1 0-2-0.9-2-2v-53.7c0-1.1 0.9-2 2-2h255.7c1.1 0 2 0.9 2 2V625c0 1.1-0.9 2-2 2zM553.3 742.5H297.6c-1.1 0-2-0.9-2-2v-53.7c0-1.1 0.9-2 2-2h255.7c1.1 0 2 0.9 2 2v53.7c0 1.1-0.9 2-2 2z" fill={color} />
      <path d="M534.1 843.5H223.4V179.7h577.2v262.1c0 10.3 5.5 19.8 14.4 24.9l0.7 0.4c18.9 10.9 42.6-2.8 42.6-24.6V122H165.7v779.2h371c23.7 0 35.8-27.4 20.8-45.7-0.2-0.2-0.4-0.4-0.5-0.7-5.7-6.9-13.9-11.3-22.9-11.3z" fill={color} />
      <path d="M677.7 866.4L623.8 902l3.9-64.4 165.9-287.4c8-13.8 25.6-18.5 39.4-10.6 13.8 8 18.5 25.6 10.6 39.4L677.7 866.4z" fill={color} />
    </svg>
  );

  const OpinionIcon = ({ color = 'currentColor' }) => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M513.422222 170.666667c-189.155556 0-341.333333 152.177778-341.333333 341.333333 0 78.222222 27.022222 155.022222 76.8 216.177778L180.622222 796.444444c-9.955556 9.955556-12.8 24.177778-7.111111 36.977778 5.688889 12.8 18.488889 19.911111 32.711111 19.911111h307.2c189.155556 0 341.333333-152.177778 341.333334-341.333333s-153.6-341.333333-341.333334-341.333333z m4.266667 614.4H288.711111l31.288889-31.288889c12.8-12.8 12.8-34.133333 0-48.355556C227.555556 612.977778 213.333333 469.333333 285.866667 359.822222c72.533333-108.088889 210.488889-150.755556 331.377777-100.977778s189.155556 177.777778 163.555556 305.777778c-25.6 126.577778-135.111111 217.6-263.111111 220.444445z" fill={color} />
    </svg>
  );

  const TutorialIcon = ({ color = 'currentColor' }) => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M512 0c282.7776 0 512 229.2224 512 512s-229.2224 512-512 512S0 794.7776 0 512 229.2224 0 512 0z m0 64a448 448 0 1 0 0 896 448 448 0 0 0 0-896z" fill={color} />
      <path d="M448.5632 440.0128l-0.256 0.1024-112.9984 251.392 258.8672-105.472 0.1024-0.256-145.7152-145.7664z m-22.016-35.072l284.7744-124.2112a25.6 25.6 0 0 1 34.048 32.8192l-115.456 293.5808a25.6 25.6 0 0 1-14.1824 14.336l-300.544 122.368a25.6 25.6 0 0 1-32.9728-34.1504l131.1744-291.7376a25.6 25.6 0 0 1 13.1072-13.0048z" fill={color} />
    </svg>
  );

  const ReviewIcon = ({ color = 'currentColor' }) => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M141.420471 344.172203c105.682823 43.452708 264.603575 108.261402 271.503707 111.075946 13.434154 5.691119 28.358289 28.936037 28.366803 53.323071v375.562755c0 8.739194-5.981817 13.3928-10.300937 13.392799-0.976698 0-1.983803-0.220152-3.077267-0.671403-109.945993-45.430429-258.417418-108.077739-259.90375-108.705356l-0.097305-0.041355-0.097304-0.040138c-14.307465-5.958707-26.395163-30.38223-26.395164-53.332801V344.172203m-44.819841-66.987108v457.551842c0 35.879956 18.562121 79.955415 53.982311 94.70805 0 0 148.833886 62.811512 260.215126 108.834285 6.717685 2.775622 13.542406 4.06856 20.193194 4.06856 29.181732 0 55.121995-24.845583 55.121994-58.215074V508.570003c-0.015812-40.694114-24.115797-81.425935-56.051256-94.739674 0 0.001216-244.978399-99.887102-333.461369-136.645234zM879.252919 344.172203v390.563518c0 22.949354-12.087698 47.372878-26.395163 53.332801l-0.097305 0.040138-0.096089 0.041355c-1.487548 0.627616-149.957757 63.27371-259.904966 108.705356-1.093464 0.451251-2.099353 0.671404-3.07605 0.671403-4.31912 0-10.300937-4.654822-10.300937-13.392799V508.587032c0.00973-24.402846 14.933865-47.64898 28.366803-53.3401 6.900132-2.813327 165.820884-67.623238 271.503707-111.074729m44.821057-66.987108c-88.48297 36.758132-333.461369 136.64645-333.461369 136.646451-31.93546 13.313739-56.03666 54.045559-56.051256 94.739674v375.562755c0 33.367057 25.942696 58.215073 55.121995 58.215073 6.653221 0 13.473076-1.291722 20.193194-4.06856 111.38124-46.023989 260.215125-108.834285 260.215125-108.834285 35.42019-14.752635 53.983528-58.828094 53.983528-94.70805V277.185095h-0.001217zM510.337303 125.795741c17.854228 0 35.335049 2.733051 46.761073 7.311244l246.458649 98.742554-246.458649 98.742553c-11.427241 4.578194-28.908062 7.311245-46.761073 7.311245s-35.333832-2.733051-46.761073-7.311245L217.116364 231.849539l246.458649-98.742554c11.427241-4.578194 28.908062-7.311245 46.76229-7.311244m0-44.821058c-22.957869 0-45.914521 3.509057-63.430614 10.525955L96.599413 231.849539l350.306059 140.3489c17.516094 7.018114 40.473962 10.527171 63.430615 10.527171 22.957869 0 45.914521-3.509057 63.430614-10.527171l350.306059-140.3489L573.767917 91.500638c-17.516094-7.018114-40.473962-10.525955-63.430614-10.525955z" fill={color} />
    </svg>
  );

  const NewsIcon = ({ color = 'currentColor' }) => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M987.601896 1024H36.398104a36.398104 36.398104 0 0 1-36.398104-36.398104V36.398104A36.398104 36.398104 0 0 1 36.398104 0h951.203792a36.398104 36.398104 0 0 1 36.398104 36.398104v951.203792a36.398104 36.398104 0 0 1-36.398104 36.398104zM72.796209 951.203791h878.407582V72.796209H72.796209z" fill={color} />
      <path d="M140.132701 324.331374V145.325498h46.541043L272.597536 254.78673V145.349763h46.298388v179.005877h-46.298388l-85.948057-109.436967v109.436967z" fill={color} />
    </svg>
  );

  const InterviewIcon = ({ color = 'currentColor' }) => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M660.157341 287.933805c24.717961 0 44.754297 20.037359 44.754297 44.754297 0 11.649318-4.453429 22.261003-11.746532 30.221302-18.329461-23.839965-23.509437-45.458331-66.573231-59.82248C634.793674 293.79121 646.790916 287.933805 660.157341 287.933805z" fill={color} />
      <path d="M734.747153 869.734544c0-125.873826-98.593506-238.689241-223.769436-238.689241-125.164674 0-208.850655 112.815415-208.850655 238.689241-11.746532 0.527003-28.491914-0.375553-44.757367 0 0-144.769174 109.667725-283.443537 253.606998-283.443537 143.964856 0 268.523733 138.674363 268.523733 283.443537C765.045203 870.617657 748.299821 868.823801 734.747153 869.734544z" fill={color} />
      <path d="M518.436596 287.933805c94.746898 0 171.557284 76.80834 171.557284 171.555238s-76.809363 171.555238-171.557284 171.555238c-94.748944 0-171.557284-76.80834-171.557284-171.555238S423.687652 287.933805 518.436596 287.933805z" fill={color} />
    </svg>
  );

  const ComparisonIcon = ({ color = 'currentColor' }) => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M744.36428 543.567694c-8.493245-8.493245-42.466226-16.98649-84.932452-33.972981-33.972981-8.493245-42.466226-8.493245-50.959471-8.493245-8.493245-8.493245-16.98649-16.98649-16.98649-25.479736 0-8.493245 0-16.98649 8.493245-16.98649 8.493245-8.493245 25.479736-8.493245 42.466226-8.493246s33.972981 0 42.466226 8.493246c8.493245 8.493245 16.98649 16.98649 16.98649 33.972981v8.493245h76.439207v-16.986491c0-33.972981-16.98649-67.945962-42.466226-84.932452-16.98649-8.493245-50.959471-16.98649-84.932452-16.98649s-67.945962 8.493245-93.425697 25.479735c-25.479736 16.98649-42.466226 42.466226-42.466226 76.439207 0 25.479736 16.98649 50.959471 42.466226 67.945962 8.493245 8.493245 33.972981 16.98649 76.439207 25.479736 42.466226 16.98649 50.959471 25.479736 59.452716 25.479735 16.98649 8.493245 16.98649 16.98649 16.986491 25.479736 0 8.493245 0 8.493245-8.493246 16.98649-16.98649 8.493245-33.972981 8.493245-50.959471 8.493246-25.479736 0-33.972981 0-50.959471-8.493246-8.493245-8.493245-8.493245-25.479736-16.986491-42.466226v-16.98649h-76.439207v16.98649c0 42.466226 16.98649 76.439207 50.959472 93.425698 25.479736 16.98649 59.452717 25.479736 93.425697 25.479735 42.466226 0 76.439207-8.493245 101.918943-25.479735 25.479736-16.98649 33.972981-42.466226 33.972981-67.945962 0-33.972981-16.98649-59.452717-42.466227-84.932452z" fill={color} />
      <path d="M387.647981 127.568543c118.905433-33.972981 237.810866-16.98649 331.236564 42.466226 25.479736 16.98649 50.959471 8.493245 67.945962-8.493245 16.98649-25.479736 8.493245-59.452717-16.986491-67.945962-127.398678-84.932452-288.770337-101.918943-441.648751-42.466226-237.810866 84.932452-373.70279 339.729809-314.250073 586.03392 16.98649 67.945962 50.959471 135.891923 84.932452 186.851395 16.98649 25.479736 50.959471 25.479736 67.945962 0 16.98649-16.98649 16.98649-42.466226 0-59.452717-8.493245-33.972981-25.479736-67.945962-42.466226-101.918942-84.932452-220.824376 42.466226-467.128487 263.290601-535.074449z" fill={color} />
    </svg>
  );

  const RoundupIcon = ({ color = 'currentColor' }) => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M886.568421 94.315789c0-32.336842-26.947368-59.284211-59.28421-59.28421H323.368421 315.284211c-2.694737 0-2.694737 2.694737-2.694737 2.694737s-2.694737 0-2.694737 2.694737L142.821053 199.410526s-2.694737 2.694737-2.694737 5.389474v727.578947c0 32.336842 26.947368 59.284211 59.28421 59.284211h633.263158c32.336842 0 59.284211-26.947368 59.284211-59.284211v-24.252631c0-10.778947-8.084211-18.863158-18.863158-18.863158s-18.863158 8.084211-18.863158 18.863158v24.252631c0 10.778947-10.778947 21.557895-21.557895 21.557895H196.715789c-10.778947 0-21.557895-10.778947-21.557894-21.557895v-700.631579h91.621052c40.421053 0 72.757895-32.336842 72.757895-72.757894V72.757895h485.052632c10.778947 0 21.557895 10.778947 21.557894 21.557894v619.789474c0 10.778947 8.084211 18.863158 18.863158 18.863158s18.863158-8.084211 18.863158-18.863158v-619.789474z m-619.789474 99.705264H202.105263l102.4-97.010527v59.284211c0 21.557895-16.168421 37.726316-37.726316 37.726316z" fill={color} />
      <path d="M455.410526 485.052632c-10.778947 0-18.863158 8.084211-18.863158 18.863157v51.2c0 10.778947 8.084211 18.863158 18.863158 18.863158s18.863158-8.084211 18.863158-18.863158v-51.2c0-10.778947-8.084211-18.863158-18.863158-18.863157zM538.947368 404.210526c-10.778947 0-18.863158 8.084211-18.863157 18.863158v132.042105c0 10.778947 8.084211 18.863158 18.863157 18.863158s18.863158-8.084211 18.863158-18.863158v-132.042105c0-10.778947-8.084211-18.863158-18.863158-18.863158zM622.484211 444.631579c-10.778947 0-18.863158 8.084211-18.863158 18.863158v91.621052c0 10.778947 8.084211 18.863158 18.863158 18.863158s18.863158-8.084211 18.863157-18.863158v-91.621052c0-10.778947-10.778947-18.863158-18.863157-18.863158z" fill={color} />
      <path d="M695.242105 342.231579c-40.421053-40.421053-97.010526-64.673684-156.294737-64.673684s-113.178947 21.557895-156.294736 64.673684c-40.421053 40.421053-64.673684 97.010526-64.673685 156.294737 0 59.284211 21.557895 113.178947 64.673685 156.294737 40.421053 40.421053 97.010526 64.673684 156.294736 64.673684 51.2 0 102.4-18.863158 140.126316-51.2l175.157895 177.852631c2.694737 2.694737 8.084211 5.389474 13.473684 5.389474s10.778947-2.694737 13.473684-5.389474c8.084211-8.084211 8.084211-18.863158 0-26.947368l-175.157894-177.852632c32.336842-40.421053 51.2-88.926316 51.2-142.821052 2.694737-59.284211-21.557895-115.873684-61.978948-156.294737zM409.6 625.178947c-35.031579-35.031579-53.894737-80.842105-53.894737-129.347368 0-48.505263 18.863158-94.315789 53.894737-129.347368s80.842105-53.894737 129.347368-53.894737c48.505263 0 94.315789 18.863158 129.347369 53.894737 35.031579 35.031579 53.894737 80.842105 53.894737 129.347368 0 48.505263-18.863158 94.315789-53.894737 129.347368-35.031579 35.031579-80.842105 53.894737-129.347369 53.894737s-94.315789-18.863158-129.347368-53.894737z" fill={color} />
    </svg>
  );

  const UltimateGuideIcon = ({ color = 'currentColor' }) => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M580.266667 580.266667L384 686.933333l106.666667-196.266666L682.666667 388.266667l-102.4 192zM533.333333 896C332.8 896 170.666667 733.866667 170.666667 533.333333S332.8 170.666667 533.333333 170.666667 896 332.8 896 533.333333 733.866667 896 533.333333 896z m0-42.666667c174.933333 0 320-145.066667 320-320S708.266667 213.333333 533.333333 213.333333 213.333333 358.4 213.333333 533.333333 358.4 853.333333 533.333333 853.333333z" fill={color} />
    </svg>
  );

  const ThoughtLeadershipIcon = ({ color = 'currentColor' }) => (
    <svg className="w-6 h-6" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <path d="M453.12 458.24c33.28 0 58.88-25.6 58.88-58.88s-25.6-58.88-58.88-58.88c-33.28 0-58.88 25.6-58.88 58.88 0 30.72 25.6 58.88 58.88 58.88zM860.16 399.36c0-33.28-25.6-58.88-58.88-58.88-33.28 0-58.88 25.6-58.88 58.88s25.6 58.88 58.88 58.88c33.28 0 58.88-28.16 58.88-58.88zM686.08 399.36c0-33.28-25.6-58.88-58.88-58.88-33.28 0-58.88 25.6-58.88 58.88s25.6 58.88 58.88 58.88 58.88-28.16 58.88-58.88zM79.36 793.6c2.56-12.8-7.68-23.04-20.48-25.6-12.8-2.56-23.04 7.68-25.6 20.48v5.12c0 30.72 12.8 58.88 35.84 81.92-35.84 25.6-61.44 66.56-66.56 110.08-2.56 12.8 7.68 23.04 17.92 25.6h2.56c10.24 0 20.48-7.68 23.04-20.48 5.12-33.28 23.04-64 51.2-84.48 10.24-7.68 17.92-17.92 17.92-30.72s-5.12-23.04-12.8-30.72c-15.36-12.8-25.6-30.72-23.04-51.2z" fill={color} />
      <path d="M53.76 768c12.8 0 23.04-7.68 23.04-20.48v-2.56c0-35.84 28.16-66.56 66.56-66.56h38.4c35.84 0 66.56 30.72 66.56 66.56 2.56 12.8 12.8 20.48 25.6 20.48 10.24 0 17.92-10.24 20.48-20.48 0-61.44-48.64-110.08-110.08-110.08H143.36c-61.44 0-110.08 48.64-110.08 110.08 0 12.8 10.24 23.04 20.48 23.04zM258.56 875.52c23.04-20.48 35.84-51.2 35.84-79.36 2.56-12.8-7.68-23.04-20.48-25.6-12.8-2.56-23.04 7.68-25.6 20.48v5.12c0 20.48-10.24 38.4-25.6 51.2-17.92 12.8-17.92 38.4-5.12 53.76l7.68 7.68c28.16 17.92 46.08 48.64 51.2 84.48 2.56 10.24 10.24 20.48 23.04 20.48h2.56c12.8-2.56 20.48-12.8 20.48-25.6-5.12-46.08-30.72-87.04-64-112.64z" fill={color} />
      <path d="M97.28 770.56v7.68c2.56 12.8 12.8 20.48 25.6 20.48 10.24 0 17.92-10.24 20.48-20.48v-7.68c2.56-12.8-7.68-23.04-20.48-25.6-12.8-2.56-23.04 7.68-25.6 20.48v5.12zM184.32 770.56v7.68c2.56 12.8 12.8 20.48 25.6 20.48 10.24 0 17.92-10.24 20.48-20.48v-7.68c2.56-12.8-7.68-23.04-20.48-25.6-12.8-2.56-23.04 7.68-25.6 20.48v5.12z" fill={color} />
      <path d="M197.12 798.72c-10.24-5.12-23.04-2.56-28.16 7.68h-10.24c-7.68-10.24-20.48-12.8-30.72-7.68s-15.36 20.48-7.68 30.72c10.24 15.36 25.6 23.04 43.52 23.04s33.28-7.68 43.52-23.04c5.12-10.24 0-23.04-10.24-30.72zM627.2 12.8c-89.6 0-176.64 30.72-248.32 84.48-92.16 71.68-145.92 184.32-145.92 302.08 0 61.44 15.36 120.32 43.52 174.08 7.68 12.8 23.04 17.92 35.84 10.24 12.8-7.68 17.92-23.04 10.24-35.84-25.6-46.08-35.84-97.28-35.84-151.04 0-102.4 48.64-197.12 128-261.12 61.44-48.64 138.24-74.24 215.04-74.24 186.88-2.56 340.48 145.92 343.04 335.36-2.56 186.88-156.16 337.92-343.04 335.36-79.36 0-153.6-25.6-215.04-74.24-10.24-10.24-28.16-7.68-35.84 2.56-10.24 10.24-7.68 28.16 2.56 35.84 71.68 56.32 158.72 87.04 248.32 87.04 215.04 2.56 391.68-171.52 394.24-386.56C1018.88 184.32 842.24 10.24 627.2 12.8z" fill={color} />
    </svg>
  );

  const blogTypes = [
    { id: 'how-to', name: 'How-to Guide', icon: <HowToIcon color="#3b82f6" />, description: 'Step-by-step instructional content', color: 'text-blue-600' },
    { id: 'listicle', name: 'Listicle', icon: <ListicleIcon color="#ec4899" />, description: 'Number-based list format (e.g., "10 Ways to...")', color: 'text-pink-600' },
    { id: 'case-study', name: 'Case Study', icon: <CaseStudyIcon color="#10b981" />, description: 'In-depth analysis with real examples', color: 'text-green-600' },
    { id: 'opinion', name: 'Opinion/Editorial', icon: <OpinionIcon color="#8b5cf6" />, description: 'Personal perspective or commentary', color: 'text-purple-600' },
    { id: 'tutorial', name: 'Tutorial', icon: <TutorialIcon color="#f97316" />, description: 'Educational step-by-step guide', color: 'text-orange-600' },
    { id: 'review', name: 'Product Review', icon: <ReviewIcon color="#eab308" />, description: 'Detailed product/service evaluation', color: 'text-yellow-600' },
    { id: 'news', name: 'News/Announcement', icon: <NewsIcon color="#64748b" />, description: 'Industry news or company updates', color: 'text-slate-600' },
    { id: 'interview', name: 'Interview', icon: <InterviewIcon color="#6366f1" />, description: 'Q&A format with experts', color: 'text-indigo-600' },
    { id: 'comparison', name: 'Comparison', icon: <ComparisonIcon color="#f59e0b" />, description: 'Side-by-side comparison of options', color: 'text-amber-600' },
    { id: 'roundup', name: 'Roundup', icon: <RoundupIcon color="#059669" />, description: 'Collection of resources or tools', color: 'text-emerald-600' },
    { id: 'ultimate-guide', name: 'Ultimate Guide', icon: <UltimateGuideIcon color="#14b8a6" />, description: 'Comprehensive deep-dive content', color: 'text-teal-600' },
    { id: 'thought-leadership', name: 'Thought Leadership', icon: <ThoughtLeadershipIcon color="#06b6d4" />, description: 'Industry insights and predictions', color: 'text-cyan-600' }
  ];

  const toneOptions = [
    { value: 'informative', label: 'Informative', description: 'Clear and educational' },
    { value: 'conversational', label: 'Conversational', description: 'Friendly and approachable' },
    { value: 'professional', label: 'Professional', description: 'Formal and authoritative' },
    { value: 'enthusiastic', label: 'Enthusiastic', description: 'Energetic and passionate' },
    { value: 'analytical', label: 'Analytical', description: 'Data-driven and logical' },
    { value: 'storytelling', label: 'Storytelling', description: 'Narrative and engaging' },
    { value: 'inspirational', label: 'Inspirational', description: 'Motivating and uplifting' },
    { value: 'humorous', label: 'Humorous', description: 'Light and entertaining' }
  ];

  const writingStyles = [
    { value: 'conversational', label: 'Conversational' },
    { value: 'academic', label: 'Academic' },
    { value: 'journalistic', label: 'Journalistic' },
    { value: 'technical', label: 'Technical' },
    { value: 'creative', label: 'Creative' },
    { value: 'business', label: 'Business' }
  ];

  const wordCountOptions = [
    { value: '300-500', label: '300-500 words (Short)' },
    { value: '500-800', label: '500-800 words (Medium-Short)' },
    { value: '800-1000', label: '800-1,000 words (Medium)' },
    { value: '1000-1500', label: '1,000-1,500 words (Standard)' },
    { value: '1500-2000', label: '1,500-2,000 words (Long)' },
    { value: '2000-2500', label: '2,000-2,500 words (Very Long)' },
    { value: '2500+', label: '2,500+ words (Comprehensive)' }
  ];

  const ctaTypes = [
    { value: 'read-more', label: 'Read More Articles' },
    { value: 'subscribe', label: 'Subscribe to Newsletter' },
    { value: 'download', label: 'Download Resource' },
    { value: 'contact', label: 'Contact Us' },
    { value: 'try-product', label: 'Try Our Product' },
    { value: 'share', label: 'Share This Post' },
    { value: 'comment', label: 'Leave a Comment' },
    { value: 'custom', label: 'Custom CTA' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English', flag: '🇬🇧' },
    { value: 'zh', label: '中文 (Chinese)', flag: '🇨🇳' },
    { value: 'es', label: 'Español (Spanish)', flag: '🇪🇸' },
    { value: 'fr', label: 'Français (French)', flag: '🇫🇷' },
    { value: 'de', label: 'Deutsch (German)', flag: '🇩🇪' },
    { value: 'ja', label: '日本語 (Japanese)', flag: '🇯🇵' },
    { value: 'ko', label: '한국어 (Korean)', flag: '🇰🇷' },
    { value: 'pt', label: 'Português (Portuguese)', flag: '🇵🇹' },
    { value: 'ru', label: 'Русский (Russian)', flag: '🇷🇺' },
    { value: 'ar', label: 'العربية (Arabic)', flag: '🇸🇦' },
    { value: 'hi', label: 'हिन्दी (Hindi)', flag: '🇮🇳' },
    { value: 'it', label: 'Italiano (Italian)', flag: '🇮🇹' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleGenerate = async () => {
    // Validation
    if (!formData.topic.trim()) {
      setError('Please enter a blog topic');
      return;
    }

    if (!formData.targetAudience.trim()) {
      setError('Please specify your target audience');
      return;
    }

    // Check authentication before making API call
    if (!checkAuthBeforeAction(navigate)) {
      return;
    }

    // Check feature permission
    if (!hasFeatureAccess('blog-posts')) {
      const planInfo = getPlanInfo();
      setUpgradePromptData({
        type: 'feature',
        featureName: 'Blog Posts',
        currentPlan: planInfo.name,
        requiredPlan: getRequiredPlanForFeature('blog-posts')
      });
      setShowUpgradePrompt(true);
      return;
    }

    setError('');
    setIsGenerating(true);

    // 根据内容模式设置不同的状态提示
    const isImageMode = formData.contentMode === 'text-with-images';
    setGenerationStatus(
      isImageMode
        ? '📝 Step 1/3: Generating blog text...'
        : '📝 Generating blog content...'
    );

    try {
      // Simulate progress updates (since we can't get real-time updates from the backend)
      const statusInterval = setInterval(() => {
        setGenerationStatus((prevStatus) => {
          if (isImageMode) {
            // 图文模式: 3步流程
            if (prevStatus.includes('Step 1/3')) {
              return '🔍 Step 2/3: Analyzing content for image placements...';
            } else if (prevStatus.includes('Step 2/3')) {
              return '🎨 Step 3/3: Generating images...';
            }
          } else {
            // 纯文本模式: 单步流程
            if (prevStatus.includes('Generating blog content')) {
              return '✍️ Writing blog content...';
            }
          }
          return prevStatus;
        });
      }, isImageMode ? 8000 : 5000); // 图文模式8秒,纯文本5秒

      const response = await contentAPI.generateBlogPost(formData);

      clearInterval(statusInterval);
      setGenerationStatus(
        isImageMode
          ? '✅ Blog with images generation complete!'
          : '✅ Blog generation complete!'
      );

      setTimeout(() => {
        setGeneratedContent(response.data.data);
        setGenerationStatus('');
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate blog post. Please try again.');
      console.error('Blog generation error:', err);
      setGenerationStatus('');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedStates(prev => ({ ...prev, [key]: true }));
    setTimeout(() => {
      setCopiedStates(prev => ({ ...prev, [key]: false }));
    }, 2000); // 2秒后恢复
  };

  const handleDownloadImage = async (imageUrl, index) => {
    try {
      // 获取图片
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // 从URL中提取文件名,或使用默认名称
      const filename = imageUrl.split('/').pop() || `blog-image-${index + 1}.png`;
      link.download = filename;

      // 触发下载
      document.body.appendChild(link);
      link.click();

      // 清理
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download image. Please try again.');
    }
  };

  const handleCopyAll = () => {
    if (generatedContent) {
      const fullText = `${generatedContent.title}\n\n${generatedContent.metaDescription ? `Meta Description: ${generatedContent.metaDescription}\n\n` : ''}${generatedContent.fullContent}`;
      navigator.clipboard.writeText(fullText);
      setCopiedStates(prev => ({ ...prev, 'all': true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, 'all': false }));
      }, 2000);
    }
  };

  return (
    <>
      <NavigationWarningModal
        isOpen={navigationWarning.showModal}
        onCancel={navigationWarning.handleCancelLeave}
        onConfirm={navigationWarning.handleConfirmLeave}
        message={navigationWarning.warningMessage}
        taskType={navigationWarning.taskType}
      />

      <UpgradePrompt
        show={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        type={upgradePromptData.type}
        featureName={upgradePromptData.featureName}
        currentPlan={upgradePromptData.currentPlan}
        requiredPlan={upgradePromptData.requiredPlan}
      />

      <div className="flex h-screen bg-gray-50">
        <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
                <svg className="w-8 h-8 mr-2" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg">
                  <path d="M512 348.028928c-64.937984 0-124.901376 21.38112-173.221888 57.491456V146.255872c0-27.404288-26.079232-49.620992-58.232832-49.620992-32.147456 0-58.228736 22.216704-58.228736 49.620992V651.204608c0 2.799616 0.28672 5.541888 0.811008 8.214528 11.114496 149.829632 136.2432 267.945984 288.872448 267.945984 160.02048 0 289.681408-129.691648 289.681408-289.66912S672.02048 348.028928 512 348.028928z m0 479.862784c-90.0096 0-165.451776-62.5664-185.174016-146.5856a190.754816 190.754816 0 0 1-5.031936-43.608064c0-28.049408 6.080512-54.677504 16.984064-78.647296 29.93152-65.796096 96.251904-111.54432 173.221888-111.54432 105.070592 0 190.208 85.153792 190.208 190.193664 0 105.035776-85.137408 190.191616-190.208 190.191616z" fill="currentColor" />
                </svg>
                Blog Posts Generator
              </h1>
              <p className="text-gray-600">
                Create SEO-optimized, engaging blog content that resonates with your audience
              </p>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Input Form */}
              <div className="space-y-6">
                {/* Blog Type Selection */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Blog Type</h2>
                  <div className="grid grid-cols-2 gap-3">
                    {blogTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setFormData(prev => ({ ...prev, blogType: type.id }))}
                        className={`p-4 rounded-lg border-2 transition text-left ${
                          formData.blogType === type.id
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="text-2xl">{type.icon}</div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 text-sm">{type.name}</div>
                            <div className="text-xs text-gray-500 mt-1">{type.description}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Content Mode Selection */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                      <path d="M574.8 537.5H228c-2.1 0-2.1 0-2.1 2.1v37.2c0 2.1 0 2.1 2.1 2.1h346.8l2.1-2.1v-37.2c0-2.1 0-2.1-2.1-2.1zM574.8 457.6H228c-2.1 0-2.1 0-2.1 2.1v37.2c0 2.1 0 2.1 2.1 2.1h346.8l2.1-2.1v-37.2c0-2.1 0-2.1-2.1-2.1zM228 410.6h119.7l2.1-2.1v-57.8c0-2.1 0-2.1-2.1-2.1H228c-2.1 0-2.1 0-2.1 2.1v57.8s0 2.1 2.1 2.1z" fill="#1890ff" />
                      <path d="M593.4 243.2h-384c-33 0-61.9 28.9-61.9 61.9v349.7L292 803.4h301.4c33 0 61.9-28.9 61.9-61.9V305.1c0-33-28.9-61.9-61.9-61.9zM211.1 663h95.4v97l-95.4-97zM614 741.5c0 10.3-10.3 20.6-20.6 20.6H347.7V642.4c0-12.4-8.3-20.6-20.6-20.6H186.7V305.1c0-10.3 10.3-20.6 20.6-20.6h386c10.3 0 20.6 10.3 20.6 20.6v436.4h0.1z" fill="#1890ff" />
                      <path d="M755.3 310.8H633V352.1h122.3c10.3 0 20.6 10.3 20.6 20.6V809.1c0 10.3-10.3 20.6-20.6 20.6H428.1c-10.3 0-20.6-10.3-20.6-20.6v-29.6h-39.2V809.1c0 33 28.9 61.9 61.9 61.9h325.1c33 0 61.9-28.9 61.9-61.9V372.7c0-33-28.9-61.9-61.9-61.9z" fill="#1890ff" />
                    </svg>
                    Content Mode
                  </h2>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        contentMode: 'text-only'
                      }))}
                      className={`p-4 rounded-lg border-2 transition text-left ${
                        formData.contentMode === 'text-only'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <svg className="w-8 h-8" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                          <path d="M384.8192 785.6128c20.7872-25.8048 45.2608-49.4592 68.5056-73.1136 63.2832-64.4096 126.6688-128.9216 189.952-193.3312 59.0848-60.1088 118.1696-120.2176 177.152-180.3264 13.2096-13.4144 25.8048-29.7984 40.5504-41.472 15.36-12.0832 25.6-4.5056 37.5808 7.7824 10.5472 10.8544 20.7872 22.016 31.1296 32.9728 2.3552 2.56 15.9744 13.2096 15.9744 16.896 0 0-0.3072-258.4576-0.3072-258.4576 0-38.6048-31.4368-70.0416-70.0416-70.0416L135.7824 26.5216c-38.6048 0-70.0416 31.4368-70.0416 70.0416l0 812.6464c0 38.6048 31.4368 70.0416 70.0416 70.0416l183.7056 0.9216 36.4544-136.704C356.0448 843.4688 364.544 810.8032 384.8192 785.6128zM461.2096 432.2304 207.36 432.2304c-11.9808 0-21.7088-9.728-21.7088-21.7088 0-11.9808 9.728-21.7088 21.7088-21.7088l253.8496 0c11.9808 0 21.7088 9.728 21.7088 21.7088C483.0208 422.5024 473.1904 432.2304 461.2096 432.2304zM637.952 317.6448 207.36 317.6448c-11.9808 0-21.7088-9.728-21.7088-21.7088 0-11.9808 9.728-21.7088 21.7088-21.7088L637.952 274.2272c11.9808 0 21.7088 9.728 21.7088 21.7088C659.6608 307.9168 649.9328 317.6448 637.952 317.6448zM758.1696 202.9568 207.36 202.9568c-11.9808 0-21.7088-9.728-21.7088-21.7088 0-11.9808 9.728-21.7088 21.7088-21.7088l550.8096 0c11.9808 0 21.7088 9.728 21.7088 21.7088C779.8784 193.2288 770.1504 202.9568 758.1696 202.9568z" fill="#13227a" />
                          <path d="M992.5632 501.4528 891.4944 400.384c-10.752-10.752-29.5936-10.752-40.3456 0L428.2368 823.0912c-1.7408 1.7408-3.072 3.9936-3.6864 6.3488l-42.2912 151.6544c-1.3312 4.8128-0.1024 10.0352 3.2768 13.7216 2.7648 2.9696 6.656 4.7104 10.6496 4.7104 0.9216 0 1.7408-0.1024 2.6624-0.2048L562.176 968.704c2.8672-0.512 5.5296-1.9456 7.5776-3.9936l422.912-422.8096C1003.7248 530.7392 1003.7248 512.6144 992.5632 501.4528zM927.1296 585.3184c-5.4272 5.4272-13.9264 5.7344-19.0464 0.6144l-100.864-100.864c-5.12-5.12-4.8128-13.6192 0.6144-19.0464 5.4272-5.4272 14.0288-5.7344 19.0464-0.6144l100.864 100.864C932.864 571.2896 932.5568 579.8912 927.1296 585.3184z" fill="#13227a" />
                        </svg>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">Text Only</div>
                          <div className="text-sm text-gray-600 mt-1">
                            Generate blog with text content only
                          </div>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        contentMode: 'text-with-images'
                      }))}
                      className={`p-4 rounded-lg border-2 transition text-left ${
                        formData.contentMode === 'text-with-images'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <svg className="w-8 h-8" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                          <path d="M157.5 98.23V928.3h708.79V98.23H157.5z m644.79 766.06H221.5V162.23h580.79v702.06z" fill="#10b981" />
                          <path d="M530.31 216l-91.53 168-54.71-53-96.86 88H624z" fill="#10b981" />
                          <path d="M287.729742 262.018632a39.67 39.67 0 1 0 78.316511-12.702743 39.67 39.67 0 1 0-78.316511 12.702743Z" fill="#10b981" />
                          <path d="M284.12 482.27H624v62.78H284.12zM284.12 608.63h461.1v62.78h-461.1zM284.12 737.3h461.1v62.78h-461.1z" fill="#10b981" />
                        </svg>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">Text & Images</div>
                          <div className="text-sm text-gray-600 mt-1">
                            Generate blog with AI-generated images
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>

                  {formData.contentMode === 'text-with-images' && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm text-blue-900 font-medium">AI Image Generation Workflow</p>
                          <p className="text-xs text-blue-700 mt-1">
                            • Step 1: AI generates blog text<br/>
                            • Step 2: AI analyzes content for optimal image placements<br/>
                            • Step 3: AI generates and inserts images
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Basic Information */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Blog Topic / Title *
                      </label>
                      <input
                        type="text"
                        name="topic"
                        value={formData.topic}
                        onChange={handleInputChange}
                        placeholder="e.g., How to Build a Successful Email Marketing Campaign"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Target Audience *
                      </label>
                      <input
                        type="text"
                        name="targetAudience"
                        value={formData.targetAudience}
                        onChange={handleInputChange}
                        placeholder="e.g., Small business owners, Digital marketers, Beginners"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Main Points to Cover
                      </label>
                      <textarea
                        name="mainPoints"
                        value={formData.mainPoints}
                        onChange={handleInputChange}
                        placeholder="List the key points or sections you want to include (one per line)"
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">Optional: Help structure your blog content</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SEO Keywords (comma separated)
                      </label>
                      <input
                        type="text"
                        name="keywords"
                        value={formData.keywords}
                        onChange={handleInputChange}
                        placeholder="e.g., email marketing, campaign strategy, conversion rate"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">For better search engine optimization</p>
                    </div>
                  </div>
                </div>

                {/* Writing Preferences */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Writing Preferences</h2>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Word Count Target
                      </label>
                      <select
                        name="wordCount"
                        value={formData.wordCount}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        {wordCountOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tone
                      </label>
                      <select
                        name="tone"
                        value={formData.tone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        {toneOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label} - {option.description}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Writing Style
                      </label>
                      <select
                        name="writingStyle"
                        value={formData.writingStyle}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        {writingStyles.map(style => (
                          <option key={style.value} value={style.value}>{style.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Language
                      </label>
                      <select
                        name="language"
                        value={formData.language}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        {languageOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.flag} {option.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Select the language for your blog content</p>
                    </div>
                  </div>
                </div>

                {/* Call-to-Action */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Call-to-Action</h2>

                  <div className="space-y-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="includeCTA"
                        checked={formData.includeCTA}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include Call-to-Action</span>
                    </label>

                    {formData.includeCTA && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          CTA Type
                        </label>
                        <select
                          name="ctaType"
                          value={formData.ctaType}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          {ctaTypes.map(cta => (
                            <option key={cta.value} value={cta.value}>{cta.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <span className="text-red-600 mr-2">⚠️</span>
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                )}

                {/* Generation Status */}
                {isGenerating && generationStatus && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <svg className="animate-spin h-5 w-5 text-blue-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-900">{generationStatus}</p>
                        <p className="text-xs text-blue-600 mt-1">This may take 30-600 seconds...</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition ${
                    isGenerating
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {isGenerating ? 'Generating Blog Post...' : '✨ Generate Blog Post'}
                </button>
              </div>

              {/* Right Column - Generated Content */}
              <div className="space-y-6">
                {generatedContent ? (
                  <>
                    {/* Blog Title */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <div className="flex items-start justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">Blog Title</h2>
                        <button
                          onClick={() => handleCopy(generatedContent.title, 'title')}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                        >
                          {copiedStates['title'] ? '✓ Copied' : 'Copy'}
                        </button>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        {generatedContent.title}
                      </h3>
                    </div>

                    {/* SEO Meta Data */}
                    {formData.seoOptimized && generatedContent.seoMeta && (
                      <div className="bg-white rounded-xl shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">SEO Meta Data</h2>

                        <div className="space-y-4">
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-sm font-medium text-gray-700">Meta Title</label>
                              <button
                                onClick={() => handleCopy(generatedContent.seoMeta.metaTitle, 'metaTitle')}
                                className="text-primary-600 hover:text-primary-700 text-xs"
                              >
                                {copiedStates['metaTitle'] ? '✓ Copied' : 'Copy'}
                              </button>
                            </div>
                            <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                              {generatedContent.seoMeta.metaTitle}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {generatedContent.seoMeta.metaTitle.length} characters
                            </p>
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-sm font-medium text-gray-700">Meta Description</label>
                              <button
                                onClick={() => handleCopy(generatedContent.seoMeta.metaDescription, 'metaDescription')}
                                className="text-primary-600 hover:text-primary-700 text-xs"
                              >
                                {copiedStates['metaDescription'] ? '✓ Copied' : 'Copy'}
                              </button>
                            </div>
                            <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                              {generatedContent.seoMeta.metaDescription}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {generatedContent.seoMeta.metaDescription.length} characters
                            </p>
                          </div>

                          {generatedContent.seoMeta.focusKeywords && (
                            <div>
                              <label className="text-sm font-medium text-gray-700 mb-2 block">Focus Keywords</label>
                              <div className="flex flex-wrap gap-2">
                                {generatedContent.seoMeta.focusKeywords.map((keyword, index) => (
                                  <span
                                    key={index}
                                    className="px-3 py-1 bg-primary-50 text-primary-700 text-xs rounded-full"
                                  >
                                    {keyword}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Full Blog Content */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <div className="flex items-start justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">Blog Content</h2>
                        <button
                          onClick={handleCopyAll}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                        >
                          {copiedStates['all'] ? '✓ Copied' : 'Copy All'}
                        </button>
                      </div>

                      <div className="prose max-w-none">
                        <ReactMarkdown
                          className="text-gray-900"
                          components={{
                            // 使用自定义图片组件
                            img: BlogImage,
                            // 自定义标题渲染
                            h1: ({node, ...props}) => (
                              <h1 className="text-2xl font-bold mt-8 mb-4" {...props} />
                            ),
                            h2: ({node, ...props}) => (
                              <h2 className="text-xl font-bold mt-8 mb-4" {...props} />
                            ),
                            h3: ({node, ...props}) => (
                              <h3 className="text-lg font-semibold mt-6 mb-3" {...props} />
                            ),
                            // 自定义段落渲染
                            p: ({node, ...props}) => (
                              <p className="mb-4" {...props} />
                            ),
                            // 自定义粗体文本
                            strong: ({node, ...props}) => (
                              <strong className="font-semibold" {...props} />
                            )
                          }}
                        >
                          {generatedContent.fullContent}
                        </ReactMarkdown>
                      </div>
                    </div>

                    {/* Generated Images */}
                    {generatedContent.generatedImages && generatedContent.generatedImages.length > 0 && (
                      <div className="bg-white rounded-xl shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">🎨 Generated Images</h2>
                        <div className="grid grid-cols-1 gap-4">
                          {generatedContent.generatedImages.map((image, index) => (
                            <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                              <img
                                src={image.imageUrl}
                                alt={`Blog image ${index + 1}`}
                                className="w-full h-auto"
                                onError={(e) => {
                                  console.error('Image load error:', image.imageUrl);
                                  e.target.src = 'https://via.placeholder.com/1024x1024?text=Image+Not+Found';
                                }}
                              />
                              <div className="p-3 bg-gray-50">
                                <p className="text-xs text-gray-600">
                                  {image.prompt || 'Generated image'}
                                </p>
                                <button
                                  onClick={() => handleDownloadImage(image.imageUrl, index)}
                                  className="flex items-center space-x-1 text-primary-600 hover:text-primary-700 text-xs mt-2 font-medium"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                  </svg>
                                  <span>Download</span>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Image Generation Error */}
                    {generatedContent.imageGenerationError && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex">
                          <span className="text-yellow-600 mr-2">⚠️</span>
                          <p className="text-sm text-yellow-700">{generatedContent.imageGenerationError}</p>
                        </div>
                      </div>
                    )}

                    {/* Content Stats */}
                    {generatedContent.stats && (
                      <div className="bg-white rounded-xl shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">📊 Content Statistics</h2>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="text-2xl font-bold text-primary-600">
                              {generatedContent.stats.wordCount}
                            </div>
                            <div className="text-sm text-gray-600">Words</div>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="text-2xl font-bold text-primary-600">
                              {generatedContent.stats.readingTime}
                            </div>
                            <div className="text-sm text-gray-600">Reading Time</div>
                          </div>
                          {generatedContent.stats.headingCount && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="text-2xl font-bold text-primary-600">
                                {generatedContent.stats.headingCount}
                              </div>
                              <div className="text-sm text-gray-600">Headings</div>
                            </div>
                          )}
                          {generatedContent.stats.paragraphCount && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="text-2xl font-bold text-primary-600">
                                {generatedContent.stats.paragraphCount}
                              </div>
                              <div className="text-sm text-gray-600">Paragraphs</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <div className="text-gray-400 mb-4">
                      <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No Blog Content Yet
                    </h3>
                    <p className="text-gray-500">
                      Fill in the form and click "Generate Blog Post" to create your content
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
      </div>
    </>
  );
};

export default BlogPosts;
